import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import useIntersect from '@hooks/useIntersect';
import { WindowType } from '@typescript/index';
import SectionTitle from '@features/layout/components/SectionTitle/SectionTitle';
import DescriptionsSection from '@features/productPreview/sections/ProductDescription/components/DescriptionsSection/DescriptionsSection';
import DeliverySection from '@features/productPreview/sections/ProductDescription/components/DeliverySection/DeliverySection';
import styles from '@features/productPreview/sections/ProductDescription/ProductDescription.styles';
import { IDescriptionSection, ISectionDescriptionItem, IVideoProductSection } from '@features/productPreview/sections/ProductDescription/typescript';
import { productPageState } from '@features/productPreview/mobX/state/context';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import { VisibleSectionType } from '@features/productPreview/const';
type PropsType = {
  section: IDescriptionSection | IVideoProductSection;
  windowType: WindowType;
  hiddenTitle?: boolean;
  isCreativeProduct?: boolean;
};
const ProductDescription = ({
  section,
  windowType,
  hiddenTitle = false
}: PropsType) => {
  const state = productPageState;
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const deliveryRef = useRef<HTMLDivElement | null>(null);
  const descriptionEntry = useIntersect(descriptionRef, {
    rootMargin: '30%',
    threshold: 0.7
  });
  const deliveryEntry = useIntersect(deliveryRef, {
    rootMargin: '5%',
    threshold: 0.7
  });
  const descriptionInView = !!descriptionEntry?.isIntersecting;
  const deliveryInView = !!deliveryEntry?.isIntersecting;
  useEffect(() => {
    if (descriptionInView) {
      state.setActualVisibleSection(VisibleSectionType.PRODUCT_DESCRIPTIONS);
    } else if (deliveryInView) {
      state.setActualVisibleSection(VisibleSectionType.PRODUCT_DELIVERY_ASSEMBLY);
    }
  }, [descriptionInView, deliveryInView, state]);
  const descriptionsSection: ISectionDescriptionItem[] = section.items.filter(item => item.descriptionCode !== 'PRODUCT_DESCRIPTIONS');
  const deliverySection: ISectionDescriptionItem[] = section.items.filter(item => item.descriptionCode === 'PRODUCT_DELIVERY_ASSEMBLY');
  return <ErrorBoundary>
      <section sx={styles.section}>
        <div sx={styles.productDescriptionContainer} data-cy="ProductDescription">
          {!hiddenTitle && section.title && <>
              <SectionTitle title={section.title} />
              <div sx={styles.productDescriptionContent(true, !hiddenTitle)} id={VisibleSectionType.PRODUCT_DESCRIPTIONS} ref={descriptionRef}>
                <DescriptionsSection items={descriptionsSection} windowType={windowType} />
              </div>
            </>}
          {deliverySection.length > 0 && <div sx={styles.productDescriptionContent(false, !hiddenTitle)} id="delivery" ref={deliveryRef}>
              <DeliverySection items={deliverySection} state={state} windowType={windowType} />
            </div>}
        </div>
      </section>
    </ErrorBoundary>;
};
export default observer(ProductDescription);